import type { HasChildren } from '@meterup/atto';
import {
  backgrounds,
  Button,
  ControlGroup,
  darkThemeSelector,
  selectors,
  shadows,
} from '@meterup/atto';
import React, { useCallback, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { colors, styled } from '../stitches';

const MIN_SCALE = 0.25;
const MAX_SCALE = 8;

const Container = styled('div', {
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  background: backgrounds.dottedLight,
  backgroundColor: colors.bgApplicationLight,
  borderRadius: '$10',
  outline: 'none',

  '&:before': {
    content: '',
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
  },

  [darkThemeSelector]: {
    background: backgrounds.dottedDark,
    backgroundColor: colors.bgApplicationDark,
  },

  [selectors.focus]: {
    '&:before': {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },
});
Container.displayName = 'Container';

const Viewport = styled('div', {
  position: 'relative',
  zIndex: 0,
  width: '100%',
  height: '100%',
});
Viewport.displayName = 'PanZoomViewport';

const ControlsRegion = styled(ControlGroup, {
  position: 'absolute',
  bottom: '$16',
  right: '$16',
  zIndex: 2,
  padding: '$8 $10',
  background: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$12',

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },
});
ControlsRegion.displayName = 'ControlsRegion';

type TransformState = {
  scale: number;
  positionX: number;
  positionY: number;
};

/**
 * Pan / zoom / pinch via [`react-zoom-pan-pinch`](https://github.com/BetterTyped/react-zoom-pan-pinch)
 * — maintained, no d3, handles wheel, trackpad, and touch without custom gesture code.
 */
export function PanAndZoomRegion({ children }: HasChildren) {
  const [transform, setTransform] = useState<TransformState>({
    scale: 1,
    positionX: 0,
    positionY: 0,
  });

  const onTransformed = useCallback(
    (_ref: unknown, state: { scale: number; positionX: number; positionY: number }) => {
      setTransform(state);
    },
    [],
  );

  const canZoomIn = transform.scale < MAX_SCALE - 1e-6;
  const canZoomOut = transform.scale > MIN_SCALE + 1e-6;
  const canReset =
    transform.scale !== 1 || Math.abs(transform.positionX) > 2 || Math.abs(transform.positionY) > 2;

  return (
    <Container tabIndex={-1}>
      <TransformWrapper
        initialScale={1}
        minScale={MIN_SCALE}
        maxScale={MAX_SCALE}
        centerOnInit
        limitToBounds={false}
        wheel={{ step: 0.12 }}
        panning={{ velocityDisabled: true }}
        doubleClick={{ disabled: true }}
        onTransformed={onTransformed}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <Viewport>
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {children}
              </TransformComponent>
            </Viewport>
            <ControlsRegion size="medium">
              <Button
                variant="secondary"
                arrangement="hidden-label"
                icon="plus"
                onClick={() => zoomIn()}
                disabled={!canZoomIn}
              >
                Zoom in
              </Button>
              <Button
                variant="secondary"
                arrangement="hidden-label"
                icon="minus"
                onClick={() => zoomOut()}
                disabled={!canZoomOut}
              >
                Zoom out
              </Button>
              <Button
                variant="secondary"
                arrangement="hidden-label"
                icon="arrows-rotate"
                onClick={() => resetTransform()}
                disabled={!canReset}
              >
                Reset
              </Button>
            </ControlsRegion>
          </>
        )}
      </TransformWrapper>
    </Container>
  );
}
